import type { SimplePoolStatusValue } from "@/_components/ui/GroupCandidates/poolStatus/constants";
import { ProcessStatusValue } from "@/_components/ui/GroupCandidates/processStatus/constants";

export enum ResumeStatus {
  INITIALIZED = "INITIALIZED",
  UPLOADED = "UPLOADED",
  ANALYZING = "ANALYZING",
  FINISH_ANALYZING = "FINISH_ANALYZING",
  EVALUATING = "EVALUATING",
  FINISH_EVALUATING = "FINISH_EVALUATING",
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
  FAILED = "FAILED",
}

export interface IResumeInList {
  resume_id: string;
  name: string;
  email: string;
  latest_company: string;
  last_updated: string;
  role: string;
  is_private: boolean;
  is_disabled: boolean;
  is_sharing: boolean;
  total_score?: number;
  approval_status: "PENDING" | "APPROVED" | "REJECTED";
  belong_to: string; // userId
  total_years_of_experience: number;
  process_status: SimplePoolStatusValue;
  is_duplicate?: boolean;
  group_id?: string; // Optional group ID to track which group the resume belongs to
  group_name?: string; // Optional group name to track which group the resume belongs to
  job_applications: JobApplication[];
}

export interface ResumeInListJobSearch extends IResumeInList {
  job_applications: Array<{
    id: string;
    created_at: string;
    updated_at: string;
    jd_id: string;
    group_id: string;
    job_posting_id: string;
    resume_id: string;
    jd: {
      title: string;
      id: string;
    };
    group_title: string;
    group_color: string;
    process_status: ProcessStatusValue;
  }>;
}
export interface UniqueResumesResponse {
  resumes: IResumeInList[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ResumeByJobResponse {
  resumes: IResumeInList[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
}

export interface ResumeByJobSearchResponse {
  resumes: ResumeInListJobSearch[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ResumeJobDetails {
  id: string;
  group_id: string;
  created_at: string;
  resume_email: string;
  resume_phone: string;
  resume_name: string;
  resume_status: ResumeStatus;
  approval_status: string;
  is_latest: boolean;
  pending_reason: string | null;
  thumbnail_url_s3: string;
  is_private: boolean;
  original_url_s3: string | null;
  url_gcloud: string;
  job_id: string;
  user_id: string;
  reward_points: number;
  bookmarked: boolean;
  job_details: {
    status: string;
    created_at: string;
    start_at: string;
    finish_at: string | null;
    result: {
      professional_languages: Array<{
        name: string;
        level: string;
        certificate?: string;
        has_certificate: boolean;
      }>;
      skills: Array<{
        name: string;
        level: string;
        reason: string;
      }>;
      basic_info: {
        github: string;
        address: string;
        nationality: string;
        phone: string;
        dob: string;
        name: string;
        linkedin: string;
        facebook: string;
        email: string;
        job_title?: string;
      };
      hobbies: string[];
      educations: Array<{
        end_date: string;
        is_study_abroad: boolean;
        major: string;
        level: string;
        university: string;
        degree: string;
        start_date: string;
      }>;
      certificates?: Array<{
        name: string;
        level: string;
        expire_date?: string;
        date?: string;
        description?: string;
      }>;
      experiences: Array<{
        end_date: string;
        description: string;
        company: string;
        responsibilities: string[];
        title: string;
        start_date: string;
      }>;
      others: {
        soft_skills: string[];
        awards: string[];
      };
    };
    total_score_evaluation: number;
    profound_skills: string[];
  };
}

export interface JobApplication {
  id: string;
  created_at: string;
  updated_at: string;
  jd_id: string;
  group_id: string;
  job_posting_id: string;
  resume_id: string;
  jd: {
    id: string;
    title: string;
  };
  group_title: string;
  group_color: string;
  process_status: ProcessStatusValue;
}
export interface FullViewResume {
  download_url: string;
  resume: {
    id: string;
    created_at: string;
    resume_email: string;
    resume_phone: string;
    resume_name: string;
    resume_status: ResumeStatus;
    approval_status: string;
    is_latest: boolean;
    pending_reason: string | null;
    thumbnail_url_s3: string;
    original_url_s3: string | null;
    url_gcloud: string;
    job_id: string;
    user_id: string;
    reward_points: number;
    job_applications: JobApplication[];
    bookmarked: boolean;
    is_private: boolean;
    job_details: {
      professional_languages: Array<{
        name: string;
        level: string;
        certificate?: string;
        has_certificate: boolean;
      }>;
      skills: Array<{
        name: string;
        level: string;
        reason: string;
      }>;
      basic_info: {
        github: string;
        address: string;
        nationality: string;
        phone: string;
        dob: string;
        name: string;
        linkedin: string;
        facebook: string;
        email: string;
        job_title?: string;
      };
      certificates?: Array<{
        name: string;
        level: string;
        expire_date?: string;
        date?: string;
        description?: string;
      }>;
      hobbies: string[];
      educations: Array<{
        end_date: string;
        is_study_abroad: boolean;
        major: string;
        level: string;
        university: string;
        degree: string;
        start_date: string;
      }>;
      experiences: Array<{
        end_date: string;
        description: string;
        company: string;
        responsibilities: string[];
        title: string;
        start_date: string;
      }>;
      others: {
        soft_skills: string[];
        awards: string[];
      };
    };
  };
}

export interface PreviewResume {
  title: string;
  resume_name: string;
  group_id: string;
  nationality: string;
  experiences: {
    end_date: string;
    description: string;
    company: string;
    responsibilities: string[];
    title: string;
    start_date: string;
  }[];
  skills: {
    name: string;
    level: string;
    reason: string;
  }[];
  price: number;
  education: {
    end_date: string;
    is_study_abroad: boolean;
    major: string;
    level: string;
    university: string;
    degree: string;
    start_date: string;
  }[];
  certificates: Array<{
    name: string;
    level?: string;
    date?: string;
    expire_date?: string;
    description?: string;
  }>;
  additional_feedback: string;
  created_at: string;
  summarization: string;
  unreasonable_resume: {
    is_unreasonable: false;
    reason: "null";
  };
}
